import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  User as FirebaseUser,
} from "firebase/auth";
import { auth } from "../lib/firebase";
import { setSentryUser, clearSentryUser } from "../lib/sentry";

interface AuthUser {
  uid: string;
  email: string | null;
  emailVerified: boolean;
  orgId: string | null;
  roles: string[];
  superAdmin: boolean;
  status: string;
}

interface AuthContextType {
  user: AuthUser | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  getIdToken: () => Promise<string | null>;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        const tokenResult = await fbUser.getIdTokenResult();
        setFirebaseUser(fbUser);
        setUser({
          uid: fbUser.uid,
          email: fbUser.email,
          emailVerified: fbUser.emailVerified,
          orgId: (tokenResult.claims.org_id as string) ?? null,
          roles: (tokenResult.claims.roles as string[]) ?? [],
          superAdmin: (tokenResult.claims.super_admin as boolean) ?? false,
          status: (tokenResult.claims.status as string) ?? "active",
        });
        setSentryUser({ uid: fbUser.uid, email: fbUser.email });
      } else {
        setFirebaseUser(null);
        setUser(null);
        clearSentryUser();
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    await createUserWithEmailAndPassword(auth, email, password);
  }, []);

  const signOut = useCallback(async () => {
    await firebaseSignOut(auth);
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  }, []);

  const getIdToken = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return null;
    return currentUser.getIdToken();
  }, []);

  const refreshToken = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    const tokenResult = await currentUser.getIdTokenResult(true);
    setUser({
      uid: currentUser.uid,
      email: currentUser.email,
      emailVerified: currentUser.emailVerified,
      orgId: (tokenResult.claims.org_id as string) ?? null,
      roles: (tokenResult.claims.roles as string[]) ?? [],
      superAdmin: (tokenResult.claims.super_admin as boolean) ?? false,
      status: (tokenResult.claims.status as string) ?? "active",
    });
  }, []);

  const value = useMemo(
    () => ({
      user,
      firebaseUser,
      loading,
      signIn,
      signUp,
      signOut,
      resetPassword,
      getIdToken,
      refreshToken,
    }),
    [
      user,
      firebaseUser,
      loading,
      signIn,
      signUp,
      signOut,
      resetPassword,
      getIdToken,
      refreshToken,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
